import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";

/**
 * JB.js — Field Management System product page
 * Content sourced 1:1 from "FIELD MANAGEMENT SYSTEM — OVERVIEW"
 *
 * Drop-in page component for a MERN app.
 * No external UI libraries required — styles are scoped in the injected
 * <style> tag below (id="jb-styles"), so this file is self-contained.
 *
 * Usage (React Router example):
 *   import JB from "./pages/JB";
 *   <Route path="/field-app" element={<JB />} />
 */

const SECTIONS = [
  {
    num: "01",
    sidebarTitle: "THE PAIN",
    sidebarBody: "Paperwork steals your free time.",
    title: "Paperwork Is Slowing You Down.",
    redHeadline:
      "You are writing quotes on paper. You are chasing customers to get paid. It is a mess.",
    bullets: [
      "Scheduling your team is a nightmare.",
      "Invoices get lost or forgotten.",
      "You look unprofessional to big clients.",
    ],
    boxTitle: "THE PROBLEM",
    boxBody:
      "You are spending your nights doing paperwork instead of relaxing.",
    isLast: false,
  },
  {
    num: "02",
    sidebarTitle: "THE FIX",
    sidebarBody: "Get paid faster and easier.",
    title: "Run Your Business From Your Phone.",
    redHeadline:
      "We give you an app that handles scheduling, quoting, and getting paid in one click.",
    bullets: [
      "Send professional quotes instantly.",
      "Schedule your guys on a simple calendar.",
      "Customers pay you by credit card on the spot.",
    ],
    boxTitle: "THE SOLUTION",
    boxBody:
      "You run the whole business from your truck. No more late-night paperwork.",
    isLast: false,
  },
  {
    num: "03",
    sidebarTitle: "THE OFFER",
    sidebarBody: "We waived the $397 setup fee for you today.",
    title: "Get Your Nights Back Today.",
    redHeadline:
      "Stop chasing payments. Let the app handle the heavy lifting.",
    bullets: null,
    boxTitle: "SECRET COUPON",
    boxBody:
      "Want 20-30% off the monthly price? We only have 5 coupons left. Apply now to see if you qualify.",
    isLast: true,
  },
];

function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      el.classList.add("jb-in");
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("jb-in");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return ref;
}

function Section({ section }) {
  const ref = useReveal();
  return (
    <div className="jb-section jb-reveal" ref={ref}>
      {/* Large title */}
      <div className="jb-title-area">
        <h2 className="jb-title">{section.title}</h2>
      </div>

      {/* Divider */}
      <div className="jb-divider" />

      {/* Two-column body */}
      <div className="jb-body-grid">
        {/* Left column */}
        <div className="jb-main-col">
          <h3 className="jb-red-headline">{section.redHeadline}</h3>

          {/* Standard bullet list */}
          {section.bullets && (
            <ul className="jb-bullets">
              {section.bullets.map((b) => (
                <li key={b} className="jb-bullet">
                  <span className="jb-sq" aria-hidden="true" />
                  <span className="jb-bullet-text">{b}</span>
                </li>
              ))}
            </ul>
          )}

          {/* Pricing bullets — section 3 only */}
          {section.isLast && (
            <ul className="jb-bullets">
              <li className="jb-bullet">
                <span className="jb-sq" aria-hidden="true" />
                <span className="jb-bullet-text">
                  Setup Fee:{" "}
                  <span className="jb-strikethrough">$397</span>{" "}
                  <span className="jb-waived">WAIVED</span>
                </span>
              </li>
              <li className="jb-bullet">
                <span className="jb-sq" aria-hidden="true" />
                <span className="jb-bullet-text">Monthly: $297</span>
              </li>
            </ul>
          )}

          {/* Red box */}
          <div className="jb-red-box">
            <span className="jb-box-title">{section.boxTitle}</span>
            <p className="jb-box-body">{section.boxBody}</p>
            {section.isLast && (
              <Link to="/booking" className="jb-cta-btn">
                CLAIM YOUR TERRITORY →
              </Link>
            )}
          </div>
        </div>

        {/* Vertical rule */}
        <div className="jb-vrule" aria-hidden="true" />

        {/* Right sidebar */}
        <div className="jb-sidebar">
          <div className="jb-sidebar-num">{section.num}</div>
          <div className="jb-sidebar-label">{section.sidebarTitle}</div>
          <p className="jb-sidebar-body">{section.sidebarBody}</p>
        </div>
      </div>
    </div>
  );
}

export default function JB() {
  return (
    <div className="jb-root">
      <style id="jb-styles">{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');

        /* ===== ROOT ===== */
        .jb-root {
          background: #ffffff;
          color: #111111;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif;
          line-height: 1.6;
          overflow-x: hidden;
        }
        .jb-root * { box-sizing: border-box; margin: 0; padding: 0; }

        /* ===== REVEAL ===== */
        .jb-reveal {
          opacity: 0;
          transform: translateY(24px);
          transition: opacity 0.65s ease, transform 0.65s cubic-bezier(0.2, 0.7, 0.3, 1);
        }
        .jb-reveal.jb-in {
          opacity: 1;
          transform: translateY(0);
        }

        /* ===== SECTION ===== */
        .jb-section {
          max-width: 900px;
          margin: 0 auto;
          padding: 80px 40px;
          border-bottom: 1px solid #e8e8e8;
        }
        .jb-section:last-child {
          border-bottom: none;
        }

        /* ===== TITLE ===== */
        .jb-title-area {
          padding-bottom: 36px;
        }
        .jb-title {
          font-size: clamp(36px, 6vw, 64px);
          font-weight: 800;
          line-height: 1.05;
          letter-spacing: -0.02em;
          color: #111111;
          max-width: 14ch;
        }

        /* ===== DIVIDER ===== */
        .jb-divider {
          height: 1px;
          background: #111111;
          margin-bottom: 48px;
        }

        /* ===== BODY GRID ===== */
        .jb-body-grid {
          display: grid;
          grid-template-columns: 1fr 1px 220px;
          gap: 0 40px;
          align-items: start;
        }

        /* ===== MAIN COLUMN ===== */
        .jb-main-col {
          padding-right: 8px;
        }
        .jb-red-headline {
          font-size: clamp(20px, 2.8vw, 26px);
          font-weight: 700;
          line-height: 1.25;
          color: #d0201a;
          margin-bottom: 28px;
        }

        /* ===== BULLETS ===== */
        .jb-bullets {
          list-style: none;
          display: flex;
          flex-direction: column;
          gap: 14px;
          margin-bottom: 32px;
        }
        .jb-bullet {
          display: flex;
          align-items: flex-start;
          gap: 12px;
        }
        .jb-sq {
          flex-shrink: 0;
          width: 12px;
          height: 12px;
          background: #d0201a;
          margin-top: 4px;
        }
        .jb-bullet-text {
          font-size: 15px;
          font-weight: 700;
          color: #111111;
          line-height: 1.5;
        }

        /* Pricing specials */
        .jb-strikethrough {
          text-decoration: line-through;
          color: #888888;
          font-weight: 700;
        }
        .jb-waived {
          color: #d0201a;
          font-weight: 700;
        }

        /* ===== RED BOX ===== */
        .jb-red-box {
          background: #d0201a;
          padding: 24px 28px 28px;
        }
        .jb-box-title {
          display: block;
          font-size: 13px;
          font-weight: 800;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: #ffffff;
          margin-bottom: 12px;
        }
        .jb-box-body {
          font-size: 15px;
          font-weight: 700;
          line-height: 1.6;
          color: #ffffff;
        }

        /* ===== CTA BUTTON ===== */
        .jb-cta-btn {
          display: inline-block;
          margin-top: 20px;
          background: #ffffff;
          color: #111111;
          font-size: 13px;
          font-weight: 800;
          letter-spacing: 0.07em;
          text-transform: uppercase;
          text-decoration: none;
          padding: 13px 24px;
          transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease;
        }
        .jb-cta-btn:hover {
          background: #111111;
          color: #ffffff;
          transform: translateY(-1px);
        }
        .jb-cta-btn:focus-visible {
          outline: 2px solid #ffffff;
          outline-offset: 3px;
        }

        /* ===== VERTICAL RULE ===== */
        .jb-vrule {
          width: 1px;
          background: #cccccc;
          align-self: stretch;
          min-height: 180px;
        }

        /* ===== SIDEBAR ===== */
        .jb-sidebar {
          padding-left: 8px;
        }
        .jb-sidebar-num {
          font-size: clamp(64px, 9vw, 96px);
          font-weight: 900;
          line-height: 0.9;
          color: #d0201a;
          letter-spacing: -0.03em;
          margin-bottom: 14px;
        }
        .jb-sidebar-label {
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: #111111;
          margin-bottom: 12px;
        }
        .jb-sidebar-body {
          font-size: 14px;
          line-height: 1.7;
          color: #555555;
        }

        /* ===== RESPONSIVE ===== */
        @media (max-width: 700px) {
          .jb-section { padding: 56px 20px; }

          .jb-body-grid {
            grid-template-columns: 1fr;
            gap: 36px 0;
          }
          .jb-vrule { display: none; }
          .jb-sidebar {
            padding-left: 0;
            border-top: 1px solid #e0e0e0;
            padding-top: 28px;
          }
          .jb-sidebar-num { font-size: 72px; }
        }

        @media (prefers-reduced-motion: reduce) {
          .jb-reveal {
            opacity: 1;
            transform: none;
            transition: none;
          }
        }
      `}</style>

      {SECTIONS.map((section) => (
        <Section section={section} key={section.num} />
      ))}
    </div>
  );
}
